// Renders the top sheet toolbar controls for undo/redo, accounts, and export actions.
import React from "react";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DownloadIcon from "@mui/icons-material/Download";
import GridOnIcon from "@mui/icons-material/GridOn";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ExampleEntry } from "../exampleEntries";

interface SheetTopToolbarProps {
  undoTooltipLabel: string;
  redoTooltipLabel: string;
  canUndo: boolean;
  canRedo: boolean;
  exampleEntries: ExampleEntry[];
  exampleEntriesDisabled: boolean;
  isDemoMode: boolean;
  onSelectExample: (index: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onOpenAccountsDrawer: () => void;
  onOpenExportMenu: (event: React.MouseEvent<HTMLElement>) => void;
  exportMenuAnchorEl: HTMLElement | null;
  isExportMenuOpen: boolean;
  onCloseExportMenu: () => void;
  onExportPNG: () => void;
  onExportPDF: () => void;
  onExportXLSX: () => void;
}

const toolbarIconButtonSx = {
  borderRadius: 1,
  "&:hover": {
    backgroundColor: "action.hover",
  },
};

export default function SheetTopToolbar({
  undoTooltipLabel,
  redoTooltipLabel,
  canUndo,
  canRedo,
  exampleEntries,
  exampleEntriesDisabled,
  isDemoMode,
  onSelectExample,
  onUndo,
  onRedo,
  onOpenAccountsDrawer,
  onOpenExportMenu,
  exportMenuAnchorEl,
  isExportMenuOpen,
  onCloseExportMenu,
  onExportPNG,
  onExportPDF,
  onExportXLSX,
}: SheetTopToolbarProps) {
  const [exampleMenuAnchorEl, setExampleMenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const isExampleMenuOpen = Boolean(exampleMenuAnchorEl);

  const closeExampleMenu = () => {
    setExampleMenuAnchorEl(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1,
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 0.75,
          py: 0.75,
          borderRadius: "8px",
          border: 1,
          borderColor: "divider",
          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
        }}
      >
        <Button
          size="small"
          variant="text"
          endIcon={<ArrowDropDownIcon />}
          disabled={exampleEntriesDisabled || isDemoMode}
          onClick={(event) => setExampleMenuAnchorEl(event.currentTarget)}
          aria-haspopup="menu"
          aria-controls={isExampleMenuOpen ? "sheet-example-entries-menu" : undefined}
          aria-expanded={isExampleMenuOpen ? "true" : undefined}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Example Entries
        </Button>
        <Menu
          id="sheet-example-entries-menu"
          anchorEl={exampleMenuAnchorEl}
          open={isExampleMenuOpen}
          onClose={closeExampleMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {exampleEntries.map((entry, index) => (
            <MenuItem
              key={entry.title}
              onClick={() => {
                closeExampleMenu();
                onSelectExample(index);
              }}
            >
              {entry.title}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.75,
          py: 0.75,
          borderRadius: "8px",
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Tooltip title={undoTooltipLabel}>
          <Box component="span">
            <IconButton
              size="small"
              aria-label="undo"
              disabled={isDemoMode || !canUndo}
              sx={toolbarIconButtonSx}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={onUndo}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>

        <Tooltip title={redoTooltipLabel}>
          <Box component="span">
            <IconButton
              size="small"
              aria-label="redo"
              disabled={isDemoMode || !canRedo}
              sx={toolbarIconButtonSx}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={onRedo}
            >
              <RedoIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>

        <Tooltip title="Accounts">
          <IconButton
            size="small"
            aria-label="Accounts"
            disabled={isDemoMode}
            sx={toolbarIconButtonSx}
            onClick={onOpenAccountsDrawer}
          >
            <AccountBalanceIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Export">
          <IconButton
            size="small"
            aria-label="Export"
            disabled={isDemoMode}
            sx={toolbarIconButtonSx}
            onClick={onOpenExportMenu}
            aria-haspopup="menu"
            aria-controls={isExportMenuOpen ? "sheet-export-menu" : undefined}
            aria-expanded={isExportMenuOpen ? "true" : undefined}
          >
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              <DownloadIcon fontSize="small" />
              <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.25 }} />
            </Box>
          </IconButton>
        </Tooltip>
        <Menu
          id="sheet-export-menu"
          anchorEl={exportMenuAnchorEl}
          open={isExportMenuOpen}
          onClose={onCloseExportMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={onExportPNG}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ImageIcon fontSize="small" />
            </ListItemIcon>
            Export as PNG
          </MenuItem>
          <MenuItem onClick={onExportPDF}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            Export as PDF
          </MenuItem>
          <MenuItem onClick={onExportXLSX}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <GridOnIcon fontSize="small" sx={{ color: "success.main" }} />
            </ListItemIcon>
            Export as XLSX
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
